import { getPreviewInstance } from '@components/Navbar/DesktopNavbar.js';
import ProjectHeaderSkeleton from '@components/Projects/skeleton/ProjectHeaderSkeleton.js';
import ProjectListSkeleton from '@components/Projects/skeleton/ProjectListSkeleton.js';
import { IS_APP, IS_MOBILE } from '@constants/global.js';
import { ProjectIcon } from '@icons/index.js';
import MainDrawer from '@layouts/Drawer.js';
import NotFound from '@pages/404/index.js';
import BasePage from '@pages/BasePage.js';
import ProjectsList from '@pages/Projects/index.js';
import { route, setPreviousRoute } from '@router/index.js';
import { resizingPreview } from '@signals/CommonSignals';
import DrawerState from '@states/DrawerState';
import ProjectsState from '@states/Projects.js';
import UserState from '@states/User.js';
import { UIDiv } from '@ui';
import { getSharedProjectId } from '@utils/projectUtils';

// this is the base page for /projects
// we always show the preview for the projectsList, and navigate to the project in the preview (if projectId is present)
// the reason is to have a smooth navigation
export default class ProjectPage extends BasePage {
  constructor() {
    super();
    this.addClass('flex flex-col h-full p-2 md:p-4');
    this.#showSkeletons();
    this.isHeaderLoaded = false; // Track header loading state
    this.isContainerLoaded = false; // Track container loading state
    this.init();
    this.contentwrapper = document.getElementById('page-content-wrapper');
  }

  /**
   * Show skeleton loaders immediately for a better UX
   */
  #showSkeletons() {
    this.clear();

    // Create and add header skeleton
    this.headerSkeleton = new ProjectHeaderSkeleton();
    this.add(this.headerSkeleton);

    // Create and add list skeleton (default to list view)
    this.containerSkeleton = new ProjectListSkeleton({ rowCount: 8 });
    this.containerSkeleton.addClass('flex-1');
    this.add(this.containerSkeleton);
  }

  /**
   * Remove skeleton loaders
   */
  #removeSkeletons() {
    if (this.headerSkeleton) {
      this.headerSkeleton.dom.remove();
      this.headerSkeleton = null;
    }
    if (this.containerSkeleton) {
      this.containerSkeleton.dom.remove();
      this.containerSkeleton = null;
    }
  }

  async init() {
    this.sharedProjectId = getSharedProjectId();

    if (!this.sharedProjectId && !UserState.user) {
      setPreviousRoute(window.location.pathname + window.location.search);
      route('/login');
      return;
    }
    if (!this.sharedProjectId) {
      this.showProjectsInPreview();
      return;
    }

    try {
      // I think we basically always consider it is a sharing project link if projectId is present because it comes from url and that it is the only workflow atm where we have a projectId in the url
      const result = await ProjectsState.getProjectByShareId(
        this.sharedProjectId,
      );
      if (result?.error?.response === 'Unauthorized access to this folder') {
        this.showErrorMessage('unauthorized');
        return;
      }
      if (!result.success || !result.project) {
        this.showErrorMessage('not_found');
        return;
      }

      const project = result.project;

      // Show guidance message
      this.showGuidanceMessage(project.name);

      // Open the preview sidebar with the project
      setTimeout(() => {
        this.openProjectInPreview(project);
      }, 0);
    } catch (error) {
      console.error('Error loading project:', error);
      this.showErrorMessage('not_found');
    }
  }

  showProjectsInPreview() {
    this.#removeSkeletons();

    this.showGuidanceMessage(null);

    const preview = getPreviewInstance();
    if (
      preview &&
      preview.component?.constructor?.name === 'ProjectsList' &&
      preview.currentWidth > 0
    ) {
      return;
    }

    if (IS_MOBILE()) {
      // not as preview on mobile
      const projectsComponent = new ProjectsList();
      this.clear();
      this.add(projectsComponent);
      return;
    } else {
      const preview = getPreviewInstance();

      if (!preview) {
        console.error('Preview instance not available');
        return;
      }

      const projectsComponent = new ProjectsList();
      requestAnimationFrame(() => {
        preview.show(projectsComponent, true, window.innerWidth - 52, true);
      });
    }
  }

  showGuidanceMessage(projectName) {
    this.#removeSkeletons();

    const container = new UIDiv().setClass('text-center max-w-2xl px-4 m-auto');

    const heading = new UIDiv().setClass(
      'text-2xl font-bold mb-4 text-gray-800',
    );
    if (projectName) {
      heading.setValue(
        i18next.t('pages.projects.project') + ': ' + projectName,
      );
    } else {
      heading.setValue(i18next.t('pages.projects.projects'));
    }

    const message = new UIDiv()
      .setClass('text-lg text-gray-600 mb-6')
      .setValue(i18next.t('pages.projects.guidance_message'));

    const illustration = new ProjectIcon({ color: 'white', size: 100 });
    illustration.setClass('mx-auto mb-6 opacity-50');

    container.add(heading, message, illustration);
    this.add(container);
  }

  showErrorMessage(errorType) {
    this.#removeSkeletons();
    const notFoundPage = new NotFound(
      errorType,
      this.sharedProjectId,
      'folder',
    );
    this.contentwrapper?.replaceChildren(notFoundPage.dom);
    resizingPreview.dispatch();
  }

  openProjectInPreview(project) {
    // we dont show the navbar in the app, we can't preview
    if (IS_APP()) {
      const projectsComponent = new ProjectsList();
      this.clear();
      this.add(projectsComponent);
      if (projectsComponent.currentView) {
        projectsComponent.currentView.navigateToProject(project);
      } else {
        setTimeout(() => {
          if (projectsComponent.currentView) {
            projectsComponent.currentView.navigateToProject(project);
          }
        }, 100);
      }
      return;
    }

    if (IS_MOBILE()) {
      const projectsComponent = new ProjectsList();
      const componentKey = 'projectslist';

      const existingDrawer = DrawerState.getContent(componentKey);
      if (!existingDrawer) {
        MainDrawer.addContent(projectsComponent, componentKey);
      }

      MainDrawer.collapseSidebar(componentKey, true);

      setTimeout(() => {
        if (projectsComponent.currentView) {
          projectsComponent.currentView.navigateToProject(project);
        } else {
          setTimeout(() => {
            if (projectsComponent.currentView) {
              projectsComponent.currentView.navigateToProject(project);
            }
          }, 100);
        }
      }, 100);
    } else {
      const preview = getPreviewInstance();

      if (!preview) {
        console.error('Preview instance not available');
        return;
      }

      const projectsComponent = new ProjectsList();
      preview.show(projectsComponent, true, 500);

      setTimeout(() => {
        if (projectsComponent.currentView) {
          projectsComponent.currentView.navigateToProject(project);
        } else {
          setTimeout(() => {
            if (projectsComponent.currentView) {
              projectsComponent.currentView.navigateToProject(project);
            }
          }, 0);
        }
      }, 0);
    }
  }
}
